// Tenant map — one node per unique thing, and where copies differ. Shown on
// the Dashboard, below the tenant's status tiles.
//
// "I have this skill: which agents and repositories carry it, and are their
// copies the same?" A skill in three repos and two agents is one node with five
// connections, so sharing and drift are visible instead of hidden behind
// duplicates. Three views: tiles count instances against unique things (and
// filter the wiring), the wiring traces one thing, the matrix shows overlap.

import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { Card, CardContent, CardHeader, CardTitle, Skeleton } from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { OverlapMatrix } from "@/components/tenant-map/matrix";
import {
  type Finding,
  type Graph,
  type Kind,
  buildGraph,
  findings,
  hasDrift,
  nodesOf,
} from "@/components/tenant-map/model";
import { Chip, Wiring } from "@/components/tenant-map/wiring";
import { getTenantMap } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import { cn } from "@/lib/utils";

export function TenantMapSection() {
  const q = useQuery({ queryKey: qk.tenantMap, queryFn: getTenantMap });
  const graph = React.useMemo(() => (q.data ? buildGraph(q.data) : null), [q.data]);
  return (
    <section aria-labelledby="tenant-map-heading">
      <div className="mb-4">
        <h2 id="tenant-map-heading" className="text-lg font-semibold tracking-tight">
          Tenant map
        </h2>
        <p className="text-sm text-muted-foreground">
          Every skill, tool, knowledge base and model once, with the agents and repositories that
          carry it and where their copies differ.
        </p>
      </div>
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : !graph ? (
        <Skeleton className="h-[640px] w-full" />
      ) : graph.nodes.size === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Nothing to map yet. Create an agent, or enable a repository under GitHub.
          </CardContent>
        </Card>
      ) : (
        <TenantMapView graph={graph} />
      )}
    </section>
  );
}

function TenantMapView({ graph }: { graph: Graph }) {
  const [filter, setFilter] = React.useState<Kind | null>(null);
  return (
    <div className="space-y-6">
      <Tiles graph={graph} filter={filter} onFilter={setFilter} />
      <Card>
        <CardHeader className="space-y-2">
          <CardTitle className="text-base">Wiring</CardTitle>
          <Legend />
        </CardHeader>
        <CardContent>
          <Wiring graph={graph} filter={filter} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-base">Overlap matrix</CardTitle>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Rows carry resources; columns are the resources. Identical rows mean identical loadouts,
            and a column with one cell is an orphan. A striped cell is a copy whose content differs
            from the one most carriers share.
          </p>
        </CardHeader>
        <CardContent>
          <OverlapMatrix graph={graph} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex-row items-baseline justify-between space-y-0">
          <CardTitle className="text-base">What the data says</CardTitle>
          <span className="text-xs text-muted-foreground">computed from the map</span>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {findings(graph).map((f) => (
            <FindingCard key={f.title} finding={f} />
          ))}
        </CardContent>
      </Card>
      <p className="max-w-3xl text-xs text-muted-foreground">
        Each agent stands for its <code>latest</code> version (else its newest). Repositories are
        the ones enabled under GitHub, as of their last sync. A skill is the same skill wherever it
        has the same name; its content is compared with frontmatter and whitespace ignored.
      </p>
    </div>
  );
}

function Tiles({
  graph,
  filter,
  onFilter,
}: {
  graph: Graph;
  filter: Kind | null;
  onFilter: (k: Kind | null) => void;
}) {
  const instances = (k: Kind) => nodesOf(graph, k).reduce((sum, n) => sum + n.deg, 0);
  const skills = nodesOf(graph, "skill");
  const tools = nodesOf(graph, "tool");
  const docs = graph.data.knowledge_bases.reduce((s, k) => s + k.documents, 0);
  const tiles: { kind: Kind; n: number; label: string; detail: React.ReactNode }[] = [
    {
      kind: "agent",
      n: graph.data.agents.length,
      label: "agents",
      detail: `across ${graph.data.domains.length} domains`,
    },
    {
      kind: "repo",
      n: graph.data.repos.length,
      label: "governed repos",
      detail: "default branch at last sync",
    },
    {
      kind: "skill",
      n: skills.length,
      label: "unique skills",
      detail: (
        <>
          {instances("skill")} copies ·{" "}
          <b className="font-semibold" style={{ color: "var(--map-drift)" }}>
            {skills.filter((s) => hasDrift(graph, s.id)).length} with drift
          </b>{" "}
          · {skills.filter((s) => s.deg <= 1).length} orphan
        </>
      ),
    },
    {
      kind: "tool",
      n: tools.length,
      label: "unique tools",
      detail: `${instances("tool")} bindings · ${tools.filter((t) => t.deg <= 1).length} used by one agent or none`,
    },
    {
      kind: "kb",
      n: graph.data.knowledge_bases.length,
      label: "knowledge bases",
      detail: `${instances("kb")} bindings · ${docs} documents`,
    },
    {
      kind: "model",
      n: graph.data.models.length,
      label: "LLM providers",
      detail: `${instances("model")} agent bindings`,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      {tiles.map((t) => (
        <button
          key={t.kind}
          type="button"
          aria-pressed={filter === t.kind}
          onClick={() => onFilter(filter === t.kind ? null : t.kind)}
          className={cn(
            "rounded-md border bg-card p-3 text-left transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            filter === t.kind && "border-foreground ring-1 ring-foreground",
          )}
        >
          <div className="text-2xl font-bold tabular-nums leading-tight">{t.n}</div>
          <div className="text-xs text-muted-foreground">{t.label}</div>
          <div className="mt-1.5 text-[11px] tabular-nums text-muted-foreground/80">{t.detail}</div>
        </button>
      ))}
    </div>
  );
}

function Swatch({ background }: { background: string }) {
  return <i className="inline-block h-3 w-3 rounded-sm" style={{ background }} />;
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <Swatch background="var(--map-aviary)" />
        Aviary
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Swatch background="var(--map-claude)" />
        Claude Code
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Swatch background="var(--map-cursor)" />
        Cursor
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Swatch background="repeating-linear-gradient(135deg, var(--map-drift) 0 3px, var(--map-drift-soft) 3px 6px)" />
        copy differs (dashed edge)
      </span>
      <span>· shape: ● agent ■ repo ◆ skill or tool ⬢ knowledge or model</span>
    </div>
  );
}

function FindingCard({ finding }: { finding: Finding }) {
  return (
    <div className="min-w-0 rounded-md border p-3">
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold">
        {finding.title}
        {finding.count !== undefined && finding.tone !== "plain" ? (
          <Chip tone={finding.tone === "shared" ? "same" : finding.tone}>{finding.count}</Chip>
        ) : null}
      </div>
      <p className="text-sm text-muted-foreground">
        {finding.items.map((item, i) => (
          <React.Fragment key={item}>
            {i ? ", " : null}
            <code className="rounded bg-muted px-1 font-mono text-[11.5px] text-foreground">
              {item}
            </code>
          </React.Fragment>
        ))}
        {finding.items.length ? " " : null}
        {finding.text}
      </p>
    </div>
  );
}
